import { ApiError } from "./types";

export type AttentionMode = "live" | "unavailable";
export type AttentionGrouping = "application" | "priority" | "due";
export type AttentionBasis = "asserted" | "derived";
export type AttentionState = "open" | "resolved" | "withdrawn";

export interface AttentionResponse<T> {
  data: T;
  mode: AttentionMode;
  correlationId: string | null;
}

export interface AttentionUnavailable {
  state: "unavailable";
  error?: { code?: string; message?: string };
}

export interface AttentionSubject {
  type: string;
  id: string;
  organizationId: string;
  owningApplication: string | null;
  label: string | null;
}

export interface AttentionPermittedAction {
  action?: string;
  label?: string;
  route?: string;
  [key: string]: unknown;
}

export interface AttentionRelationshipContext {
  [key: string]: unknown;
}

export interface AttentionPresentation {
  isRead: boolean;
  isDismissed: boolean;
  isPinned: boolean;
  snoozedUntil: string | null;
}

export interface AttentionPrincipal {
  principalType: string;
  principalId: string;
  /**
   * The id the system that issued this principal knows them by, where the
   * platform holds one such mapping. Studio issues its own user ids, so this is
   * how a Studio reader names the person a projected item is addressed to.
   */
  externalPrincipalId?: string;
}

export interface AttentionWorkItem {
  id: string;
  organizationId: string;
  workItemType: string;
  subject: { type: string; id: string; label?: string };
  title: string;
  authoritativeOwner: AttentionPrincipal;
  assigneeKey: string;
  assignedTo: AttentionPrincipal | null;
  requiredPermission: string;
  route: string;
  resolutionEndpoint: string;
  permittedActions: AttentionPermittedAction[];
  priority: string;
  risk: string | null;
  dueAt: string | null;
  dueDateAuthority: string | null;
  dueDateReference: string | null;
  basis: AttentionBasis;
  confidence: number | null;
  relationshipContext: AttentionRelationshipContext[];
  state: AttentionState;
  resolvedAt: string | null;
  resolutionReportedBy: string | null;
  sourceRevision: number;
  subjectRevision: number | null;
  sourceEventId: string | null;
  raisedAt: string | null;
  recordedAt: string;
  presentation: AttentionPresentation;
  stale: boolean | null;
}

export interface AttentionWorkItems {
  workItems: AttentionWorkItem[];
  withheld: {
    not_entitled: number;
    permission_denied: number;
    permission_unresolvable: number;
  };
}

export type AttentionWorkItemsResponse = AttentionWorkItems | AttentionUnavailable;

export interface AttentionPreferences {
  grouping: AttentionGrouping;
  mutedWorkItemTypes: string[];
}

export interface AttentionPreferencesEnvelope {
  preferences: AttentionPreferences;
}

export interface AttentionBridgeRelated {
  application: string;
  objectType: string;
  basis: AttentionBasis;
  count: number | null;
  state: "available" | "unavailable";
  confidence?: number;
}

export interface AttentionBridge {
  subject: AttentionSubject;
  origin: { application: string | null; route: string | null; resolver: string | null };
  related: AttentionBridgeRelated[];
}

/** Studio global search (ODS-EWO-033 P2). It returns identity and a resolver
 * back to the owning application, never that application's content, and states
 * what it could not cover rather than presenting a filtered answer as complete. */
export interface SearchResult {
  objectType: string;
  owningApplication: string;
  objectId: string;
  title: string;
  context: { name: string; value: string }[];
  resolver: string;
  revision: number;
}

export interface SearchAnswer {
  query: string;
  results: SearchResult[];
  withheld: {
    not_entitled: number;
    restricted_visibility: number;
    uncovered_type: number;
  };
  uncoveredTypes: string[];
  truncated: boolean;
}

export type SearchResponse = SearchAnswer | AttentionUnavailable;

export interface AttentionClientOptions {
  baseUrl: string;
  getToken?: () => string | null;
  onUnauthorized?: () => void;
}

async function parse(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

export class AttentionClient {
  private readonly baseUrl: string;
  private readonly getToken: () => string | null;
  private readonly onUnauthorized: (() => void) | undefined;

  constructor(options: AttentionClientOptions) {
    this.baseUrl = options.baseUrl.replace(/\/$/, "");
    this.getToken = options.getToken ?? (() => null);
    this.onUnauthorized = options.onUnauthorized;
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    capability: "attention" | "search" = "attention",
  ): Promise<AttentionResponse<T>> {
    const headers: Record<string, string> = { Accept: "application/json" };
    const token = this.getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    if (body !== undefined) headers["Content-Type"] = "application/json";
    const init: RequestInit = { method, headers };
    if (body !== undefined) init.body = JSON.stringify(body);
    let response: Response;
    try {
      response = await fetch(`${this.baseUrl}${path}`, init);
    } catch {
      return {
        data: {
          state: "unavailable",
          error:
            capability === "search"
              ? { code: "search_unavailable", message: "Unable to reach Studio search." }
              : { code: "attention_unavailable", message: "Unable to reach the Attention Center." },
        } as T,
        mode: "unavailable",
        correlationId: null,
      };
    }
    if (response.status === 401) this.onUnauthorized?.();
    const data = await parse(response);
    const mode: AttentionMode =
      response.headers.get("x-attention-mode") === "unavailable" ||
      response.headers.get("x-search-mode") === "unavailable"
        ? "unavailable"
        : "live";
    if (!response.ok && !(response.status === 503 && mode === "unavailable")) {
      const error = (data ?? {}) as { error?: { code?: string; message?: string } };
      throw new ApiError(
        response.status,
        error.error?.code ?? "error",
        error.error?.message ?? `Request failed (${response.status}).`,
      );
    }
    return {
      data: data as T,
      mode,
      correlationId: response.headers.get("x-correlation-id"),
    };
  }

  workItems(
    orgId: string,
    filters: {
      includeDismissed?: boolean;
      includeSnoozed?: boolean;
      grouping?: AttentionGrouping;
      workItemType?: string;
      state?: string;
    } = {},
  ) {
    const query = new URLSearchParams();
    if (filters.includeDismissed !== undefined) query.set("includeDismissed", String(filters.includeDismissed));
    if (filters.includeSnoozed !== undefined) query.set("includeSnoozed", String(filters.includeSnoozed));
    if (filters.grouping) query.set("grouping", filters.grouping);
    if (filters.workItemType) query.set("workItemType", filters.workItemType);
    if (filters.state) query.set("state", filters.state);
    const suffix = query.toString() ? `?${query.toString()}` : "";
    return this.request<AttentionWorkItemsResponse>("GET", `/organizations/${orgId}/attention/work-items${suffix}`);
  }

  updatePresentation(
    orgId: string,
    workItemId: string,
    input: Partial<Pick<AttentionPresentation, "isRead" | "isDismissed" | "isPinned" | "snoozedUntil">>,
  ) {
    return this.request<{ status: string }>(
      "PATCH",
      `/organizations/${orgId}/attention/work-items/${workItemId}/presentation`,
      input,
    );
  }

  preferences(orgId: string) {
    return this.request<AttentionPreferencesEnvelope | AttentionUnavailable>("GET", `/organizations/${orgId}/attention/preferences`);
  }

  putPreferences(orgId: string, input: AttentionPreferences) {
    return this.request<AttentionPreferencesEnvelope | AttentionUnavailable>("PUT", `/organizations/${orgId}/attention/preferences`, input);
  }

  search(
    orgId: string,
    query: string,
    filters: { limit?: number; objectType?: string; application?: string } = {},
  ) {
    const params = new URLSearchParams({ q: query });
    if (filters.limit !== undefined) params.set("limit", String(filters.limit));
    if (filters.objectType) params.set("objectType", filters.objectType);
    if (filters.application) params.set("application", filters.application);
    return this.request<SearchResponse>(
      "GET",
      `/organizations/${orgId}/search?${params.toString()}`,
      undefined,
      "search",
    );
  }

  bridge(orgId: string, subjectType: string, subjectId: string) {
    return this.request<AttentionBridge>(
      "GET",
      `/organizations/${orgId}/context/subjects/${encodeURIComponent(subjectType)}/${encodeURIComponent(subjectId)}`,
    );
  }
}
